/**
 * InvoiceRouteAdmin
 * -----------------------------------------------------------------------------
 * Invoice-specific router + Formik provider for the create / edit / duplicate
 * flows.  It mirrors the pattern of the shared RouteAdmin
 * (src/screens/Applications&WorkOrders/RouteAdmin/index.tsx) but is scoped
 * entirely to the Invoice domain.
 *
 * FLOW RESOLUTION ORDER
 * 1. location.state?.flow === 'duplicate' + location.state?.invoiceId
 *    → treat as DUPLICATE (action-menu entry point)
 * 2. Props `flow` + `invoiceId` (route-level entry point)
 *
 * LOADING
 * - CREATE: initialInvoiceValues used directly; no network request.
 * - EDIT / DUPLICATE: useInvoiceFormById fetches the invoice and maps it to
 *   form values.  SpinnerModal is shown while loading.
 *
 * FORMIK onSubmit is intentionally a no-op — InvoiceStepsLayout calls
 * useInvoiceMutations directly when the user confirms the save.
 */

import React, { FC, useMemo } from 'react';
import { Formik } from 'formik';
import { useLocation } from 'react-router-dom';

import SpinnerModal from 'components/SpinnerModal';

import {
  initialInvoiceValues,
  invoiceValidationSchema
} from './Constants/invoiceFormikData';
import useInvoiceFormById from './hooks/useInvoiceFormById';
import InvoiceStepsLayout, { InvoiceMode } from './StepsLayout';

// ---------------------------------------------------------------------------
// Flow enum — invoice-specific, intentionally NOT the shared RouteAdmin Flow
// ---------------------------------------------------------------------------
export enum InvoiceFlow {
  CREATE,
  EDIT,
  DUPLICATE
}

// ---------------------------------------------------------------------------
// Location state shape written by the action-menu duplicate entry point
// ---------------------------------------------------------------------------
interface InvoiceRouteLocationState {
  flow?: string;
  invoiceId?: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface InvoiceRouteAdminProps {
  flow: InvoiceFlow;
  invoiceId?: string;
}

// ---------------------------------------------------------------------------
// Helper: map InvoiceFlow → InvoiceMode (for InvoiceStepsLayout)
// ---------------------------------------------------------------------------
const flowToMode = (flow: InvoiceFlow): InvoiceMode => {
  switch (flow) {
    case InvoiceFlow.EDIT:
      return 'edit';
    case InvoiceFlow.DUPLICATE:
      return 'duplicate';
    case InvoiceFlow.CREATE:
    default:
      return 'create';
  }
};

// ---------------------------------------------------------------------------
// Inner component — owns the useInvoiceFormById call so the hook is always
// called (never conditionally).  Rendered only after flow resolution.
// ---------------------------------------------------------------------------
interface InnerProps {
  resolvedFlow: InvoiceFlow;
  resolvedInvoiceId: string | null;
}

const InvoiceRouteAdminInner: FC<InnerProps> = ({
  resolvedFlow,
  resolvedInvoiceId
}) => {
  const mode = flowToMode(resolvedFlow);

  let hookMode: 'edit' | 'duplicate' | 'create' = 'create';
  if (resolvedFlow === InvoiceFlow.EDIT) hookMode = 'edit';
  else if (resolvedFlow === InvoiceFlow.DUPLICATE) hookMode = 'duplicate';

  const { formValues, isLoading } = useInvoiceFormById(
    resolvedInvoiceId,
    hookMode
  );

  // Seed the create form's Invoice Date with today's date at mount rather than
  // the module-load time frozen into `initialInvoiceValues` (which would drift
  // stale in a long-lived tab).
  const createValues = useMemo(
    () => ({ ...initialInvoiceValues, invoiceDate: new Date() }),
    []
  );

  const values =
    resolvedFlow === InvoiceFlow.CREATE ? createValues : formValues;

  return (
    <>
      <SpinnerModal open={isLoading} message="Loading Invoice..." />
      {!isLoading && (
        <Formik
          initialValues={values}
          validationSchema={invoiceValidationSchema}
          validateOnChange
          validateOnBlur
          onSubmit={() => {}}
        >
          {() => (
            <InvoiceStepsLayout
              mode={mode}
              invoiceId={resolvedInvoiceId ?? undefined}
            />
          )}
        </Formik>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// InvoiceRouteAdmin — public component
// ---------------------------------------------------------------------------
const InvoiceRouteAdmin: FC<InvoiceRouteAdminProps> = ({ flow, invoiceId }) => {
  const { state } = useLocation<InvoiceRouteLocationState | undefined>();

  // Action-menu duplicate entry point takes precedence over props
  const isDuplicateFromMenu = state?.flow === 'duplicate' && !!state?.invoiceId;

  const resolvedFlow: InvoiceFlow = isDuplicateFromMenu
    ? InvoiceFlow.DUPLICATE
    : flow;

  const resolvedInvoiceId: string | null = isDuplicateFromMenu
    ? (state!.invoiceId as string)
    : invoiceId ?? null;

  return (
    <InvoiceRouteAdminInner
      resolvedFlow={resolvedFlow}
      resolvedInvoiceId={resolvedInvoiceId}
    />
  );
};

export default InvoiceRouteAdmin;
